import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useLocation, Route, Switch } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { UpdateAvailableBanner } from "@/components/UpdateAvailableBanner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AuthProvider, useAuth, SIGN_IN_PATH } from "@/contexts/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { AppShell } from "@/components/AppShell";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { FFTestingBanner } from "@/components/FFTestingBanner";
import { StagingBanner } from "@/components/StagingBanner";
import { FFExplainer } from "@/components/FFExplainer";
import { DisclaimerGate } from "@/components/DisclaimerGate";
import { InterviewIntro } from "@/components/interview/InterviewIntro";
import { useInterviewGate } from "@/hooks/useInterviewGate";
import { PlanApp } from "@/components/plan/PlanApp";
import { ErrorFallback } from "@/components/ErrorFallback";
import MobileUploadPage from "@/pages/MobileUploadPage";
import SupportPage from "@/pages/SupportPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import LandingPage from "@/pages/LandingPage";
import ContactPage from "@/pages/ContactPage";
import SecurityPage from "@/pages/SecurityPage";
import PricingPage from "@/pages/PricingPage";
import AdvisorsPage from "@/pages/AdvisorsPage";
import VerifyEmailResult from "@/pages/VerifyEmailResult";
import { useToast } from "@/hooks/use-toast";
import { SensitiveDataSessionProvider } from "@/lib/sensitive-data-session-provider";
import { LogoutSaveCheckProvider, useGuardedSignOut } from "@/components/LogoutSaveCheck";
import { StopReleaseBanner } from "@/components/StopReleaseBanner";

function AppRouter() {
  const { isAuthenticated, isLoading, user, logoutTo } = useAuth();
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  // INTERVIEW-SHELL-1 (s205): declared here, above every early return, so hook order is stable.
  // Fetches nothing for non-planner roles (see useInterviewGate).
  const interview = useInterviewGate(user);
  // s227 (LOGOUT-SAVE-CHECK-1): the interview's Sign out asks first when the Secure File has unsaved changes.
  const guardedSignOut = useGuardedSignOut();

  // Handle email verification redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailVerified = params.get("emailVerified");
    if (emailVerified === "success") {
      toast({ title: "Email verified!", description: "Your email address has been verified." });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      const url = new URL(window.location.href);
      url.searchParams.delete("emailVerified");
      window.history.replaceState({}, "", url.toString());
    } else if (emailVerified === "error") {
      toast({ title: "Verification failed", description: "The verification link is invalid or has expired. Please request a new one.", variant: "destructive" });
      const url = new URL(window.location.href);
      url.searchParams.delete("emailVerified");
      window.history.replaceState({}, "", url.toString());
    }
    const passwordReset = params.get("passwordReset");
    if (passwordReset === "success") {
      toast({ title: "Password reset!", description: "Your password has been reset. Please sign in." });
      const url = new URL(window.location.href);
      url.searchParams.delete("passwordReset");
      window.history.replaceState({}, "", url.toString());
    }
  }, []); // Run once on mount

  // Authenticated users on the auth pages go to the app dashboard (not "/", which is now
  // the landing page). MUST be declared before any early return below so the hook order
  // stays stable across route changes (rules of hooks).
  useEffect(() => {
    if (isAuthenticated && (location === "/login" || location === "/signup")) {
      setLocation("/dashboard");
    }
  }, [isAuthenticated, location, setLocation]);

  // Handle public routes first (before auth check)
  if (location.startsWith("/upload/")) {
    return (
      <Switch>
        <Route path="/upload/:token" component={MobileUploadPage} />
      </Switch>
    );
  }

  if (location === "/support") {
    return <SupportPage />;
  }

  if (location === "/privacy-policy") {
    return <PrivacyPolicy />;
  }

  if (location === "/terms-of-service") {
    return <TermsOfService />;
  }

  if (location === "/contact") {
    return <ContactPage />;
  }

  if (location === "/security") {
    return <SecurityPage />;
  }

  // Pricing and Advisors as REAL routes (s216, SEO phase 1 item 2). They render the same
  // sections the landing page renders, from the shared components — these are the two URLs
  // people search for and partners link to, and before this both were scroll anchors whose
  // address stayed "/". Public (both auth states), like the landing page above.
  if (location === "/pricing") {
    return <PricingPage />;
  }

  if (location === "/advisors") {
    return <AdvisorsPage />;
  }

  // Email-verification result (XC-24) — must render in BOTH auth states (logged out,
  // logged in as the verified user, or logged in as a different user), so it lives here
  // among the public routes, before the auth gates.
  if (location === "/verify-email/result") {
    return <VerifyEmailResult />;
  }

  // Landing page lives at "/" for BOTH auth states (Option B, s90) — it sits outside
  // AuthShell/AppShell and self-detects auth (via useAuth) to swap its CTAs. The app
  // dashboard now lives at "/dashboard".
  if (location === "/") {
    return <LandingPage />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // "/" is handled above (landing page); everything else routes through AuthShell,
    // whose catch-all redirects unknown paths (e.g. /dashboard) to /login.
    return <AuthShell />;
  }

  // Disclaimer gate — must acknowledge before accessing any part of the app
  if (user && !user.disclaimerAcknowledgedAt) {
    return (
      <DisclaimerGate
        onAcknowledged={() => {
          // The query was already invalidated inside DisclaimerGate after the POST;
          // React will re-render with the updated user and this branch won't show.
        }}
      />
    );
  }

  // FF explainer reference page (banner "Learn more" target) — content only, no gate.
  if (location === "/ff-welcome") {
    return <FFExplainer mode="reference" />;
  }

  // First-login F&F explainer interstitial — F&F users only, shown once (server-persisted
  // seen flag). "Get started" POSTs the flag; the refetched user flips this condition false.
  if (user && user.isFAndF && !user.ffExplainerSeen) {
    return <FFExplainer mode="interstitial" />;
  }

  // ── INTERVIEW-SHELL-1 (s205) — the interview rungs, AFTER the F&F explainer (D-202.24 puts the
  // mode intro before chapter one, and the explainer is the rung that precedes it).
  //
  // 🔴 `loading` holds the same spinner the ladder already uses rather than falling through: without
  // it a planner mid-interview would see the dashboard paint and then be replaced by a full-screen
  // interview, which reads as the product losing their place.
  if (interview.status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  // DEATH-EMAILS-1 (s229, D-229.65): the Stop-the-release banner sits above EVERY planner surface —
  // the intro, the shell / plan home and the old dashboard — and renders nothing without a pending report.
  const stopBanner = <StopReleaseBanner userId={user?.id} isPlanner={user?.role === "client"} />;

  if (interview.status === "intro") {
    return <>{stopBanner}<InterviewIntro clientId={interview.clientData.id} /></>;
  }
  // s228 (PLAN-HOME-1, Reade ruling B): the interview in progress and the finished plan both live inside the plan's
  // frame (board 12) — header, Planner/Executor tabs, the counter on the Executor tab. Behind the same flag.
  if (interview.status === "shell" || interview.status === "done") {
    return (
      <>
      {stopBanner}
      <PlanApp
        clientData={interview.clientData}
        position={interview.status === "shell" ? interview.position : null}
        plannerName={(user?.fullName ?? "").trim().split(/\s+/)[0] ?? ""}
        onSignOut={() => guardedSignOut(() => logoutTo(SIGN_IN_PATH))}
      />
      </>
    );
  }

  const style = {
    "--sidebar-width": "20rem",
    "--sidebar-width-icon": "4rem",
  };

  return (
    <>
      {stopBanner}
      <FFTestingBanner />
      <SidebarProvider style={style as React.CSSProperties}>
        <AppShell />
        <FeedbackWidget />
      </SidebarProvider>
    </>
  );
}

/**
 * s227 (LOGOUT-SAVE-CHECK-1) — the Secure File session lives HERE, above every screen that can sign out (the
 * interview shell and the dashboards), so the sign-out check sees the same unsaved values the planner typed. It was
 * mounted inside ClientDashboard only, which left the interview's Sign out outside it.
 *
 * 🔴 Keyed by the signed-in user: a different user (or none) gets a fresh, empty session — Cat A values never carry
 * from one account to the next.
 */
function SecureFileSessionScope({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <SensitiveDataSessionProvider key={user?.id ?? "signed-out"}>
      <LogoutSaveCheckProvider>{children}</LogoutSaveCheckProvider>
    </SensitiveDataSessionProvider>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            {/* STAGING-BANNER-1 (s149): loud non-prod bar above every page (logged-out included). */}
            <StagingBanner />
            <SecureFileSessionScope>
              <AppRouter />
            </SecureFileSessionScope>
            <Toaster />
            <UpdateAvailableBanner />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
